import logger from 'loglevel';
import window from 'global/window';
import { Grid } from '@mui/material';
import React from 'react';
import deepmerge from 'deepmerge';
import {
  ENVIRONMENT_CONSTANTS,
  PRELOADED_STATE_KEY,
  AWS_ENV_VARS,
  COURSE_PREVIEW_API_HOST,
  COURSE_API_HOST,
  LEARN_LIVE_HOST_COMMON,
  CONTENTFUL_ENVIRONMENT_ID,
  LEARN_LIVE_ADMIN_APP_ID,
  CONTENTFUL_SPACE_ID,
  LAP_LEARN_LIVE_SERVICE_HOST,
} from './constants';

export const getEnvironmentConstant = ({ key, env }) => {
  if (ENVIRONMENT_CONSTANTS[env]) {
    if (ENVIRONMENT_CONSTANTS[env][key]) {
      const defaultValue = ENVIRONMENT_CONSTANTS[env][key];
      if (typeof window[PRELOADED_STATE_KEY]?.envData?.[key] !== 'undefined') {
        return window[PRELOADED_STATE_KEY]?.envData[key];
      }
      if (typeof process.env[key] !== 'undefined') {
        // Add Validation
        if (AWS_ENV_VARS[key]) {
          const processEnv = `${process.env?.[key]}`;
          if (AWS_ENV_VARS[key].default || processEnv) { // If not set and no default available hide the key
            if (typeof AWS_ENV_VARS[key].validator === 'function') {
              return AWS_ENV_VARS[key].validator(processEnv) ? processEnv : AWS_ENV_VARS[key].default;
            }
            return processEnv || AWS_ENV_VARS[key].default;
          }
        }
        return `${process.env[key]}`;
      }
      return defaultValue;
    }
    if (typeof ENVIRONMENT_CONSTANTS[env][key] === 'string') {
      return ENVIRONMENT_CONSTANTS[env][key];
    }
    logger.error(`${key} not found in environment constants config for env ${env}`);
    return null;
  }
  logger.error(`${env} environment not found`);
  return null;
};

export const getLearnLiveServiceHost = ({ env }) => (`${getEnvironmentConstant({
  env,
  key: LEARN_LIVE_HOST_COMMON,
})}`);

export const getLapLearnLiveServiceHost = ({ env }) => (`${getEnvironmentConstant({
  env,
  key: LAP_LEARN_LIVE_SERVICE_HOST,
})}`);

export const stripTrailingSlash = (str) => (str.endsWith('/') ? str.slice(0, -1) : str);

export const flatDeep = (arr, d = 1) => (d > 0
  ? arr.reduce((acc, val) => acc.concat(Array.isArray(val)
    ? flatDeep(val, d - 1) : val), [])
  : arr.slice());

export const chunkArray = (array, size) => {
  const result = [];
  for (let i = 0; i < array.length; i += size) {
    const chunk = array.slice(i, i + size);
    result.push(chunk);
  }
  return result;
};
export function shuffleArray(array) {
  const subArray = array;
  for (let i = subArray.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [subArray[i], subArray[j]] = [array[j], array[i]];
  }
}
export const calculateEffortDisplay = (totalMinutes) => {
  if (Number.isInteger(totalMinutes)) {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours > 0) {
      if (minutes > 0) {
        return `${hours} hr. ${minutes} min.`;
      }
      return `${hours} hr.`;
    }
    return `${minutes} min.`;
  }
  return null;
};

export const getCourseAPIHost = ({ env, isPreview = false, isSSR = false }) => {
  // Use same host for front-end
  if (window?.location?.origin && !window?.location?.origin.startsWith('https://localhost')) {
    if (isSSR) {
      logger.debug('getCourseAPIHost SRR: found window');
    }
    return window.location.origin;
  }

  // Handle generating a host name for SSR
  if (isPreview) {
    if (isSSR) {
      logger.debug('getCourseAPIHost SRR: isPreview', getEnvironmentConstant({ env, key: COURSE_PREVIEW_API_HOST }));
    }
    return getEnvironmentConstant({ env, key: COURSE_PREVIEW_API_HOST });
  }
  if (isSSR) {
    logger.debug('getCourseAPIHost SRR: is not preview', getEnvironmentConstant({ env, key: COURSE_API_HOST }));
  }
  return getEnvironmentConstant({ env, key: COURSE_API_HOST });
};

// eslint-disable-next-line react/prop-types
export const GridItemNestedLeft = ({ children, sx, ...props }) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <Grid item {...props} sx={{ ...sx, pl: '0px !important' }}>
    {children}
  </Grid>
);
// eslint-disable-next-line react/prop-types
export const GridItemNestedRight = ({ children, sx, ...props }) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <Grid item {...props} sx={{ ...sx, pl: { sm: '0px !important', lg: '1.5rem !important' } }}>
    {children}
  </Grid>
);

export const generateLearnLiveClassURL = ({ env, classId }) => {
  let url;
  const environment = getEnvironmentConstant({ env, key: CONTENTFUL_ENVIRONMENT_ID });
  const appId = getEnvironmentConstant({ env, key: LEARN_LIVE_ADMIN_APP_ID });
  const spaceId = getEnvironmentConstant({ env, key: CONTENTFUL_SPACE_ID });
  if (ENVIRONMENT_CONSTANTS[env] && environment && appId && spaceId) {
    if (env !== 'prod') {
      url = `https://app.contentful.com/spaces/${spaceId}/environments/${environment}/apps/app_installations/${appId}/learn-live-admin?entryId=${classId}`;
    }
    if (env === 'prod') {
      url = `https://app.contentful.com/spaces/${spaceId}/apps/app_installations/${appId}/learn-live-admin?entryId=${classId}`;
    }
  }
  return url;
};

export const convertToBoolean = ({ value, defaultValue }) => {
  let returnValue = false;
  if (typeof value === 'string') {
    returnValue = value.toLowerCase() === 'true';
  } else if (typeof value === 'boolean') {
    returnValue = value;
  } else if (typeof defaultValue === 'boolean') {
    returnValue = defaultValue;
  }
  return returnValue;
};

export const uniqueObjectByProperty = (array, property) => {
  if (!Array.isArray(array)) {
    return [];
  }
  return array.filter((v, i, a) => a.findIndex((t) => t[property] === v[property]) === i);
};

export const sortCoachListByLastName = ({ coachList }) => {
  const filteredData = uniqueObjectByProperty(coachList, 'userId');
  const result = (filteredData.length && filteredData?.sort((a, b) => {
    if (a.coachLastName.trim().toLowerCase() < b.coachLastName.trim().toLowerCase()) return -1;
    if (a.coachLastName.trim().toLowerCase() > b.coachLastName.trim().toLowerCase()) return 1;
    return 0;
  })) || [];

  return result;
};

export const adaptPicklistToCoach = ({ data }) => {
  // note: data is expected to be the coach list
  const selectOptions = data && sortCoachListByLastName({ coachList: data }).map((item) => (
    {
      children: `${item.coachFirstName} ${item.coachLastName}`,
      value: item,
      coachingTypeId: item.coachingTypeId,
      coachEmail: item.coachEmail,
      id: item.id,
      label: `${item.coachFirstName} ${item.coachLastName}`,
    }
  ));

  return selectOptions;
};

export const adaptPicklistToChip = ({ data }) => {
  if (!data || !Array.isArray(data)) {
    return [];
  }

  // Deduplicate by id first, then by label if id is missing or duplicate
  const seenIds = new Set();
  const seenLabels = new Set();

  const uniqueData = data.filter((item) => {
    // If item has an id, deduplicate by id
    if (item.id !== undefined && item.id !== null) {
      if (seenIds.has(item.id)) {
        return false;
      }
      seenIds.add(item.id);
      return true;
    }
    // If no id, deduplicate by label (case-insensitive)
    const labelKey = item.label?.toLowerCase()?.trim() || '';
    if (labelKey && seenLabels.has(labelKey)) {
      return false;
    }
    if (labelKey) {
      seenLabels.add(labelKey);
    }
    return true;
  });

  const selectOptions = uniqueData.map((item) => (
    {
      children: item.label, value: item.label, id: item.id, label: item.label,
    }
  ));

  return selectOptions;
};

export const getCoachName = ({ userId, coachList }) => {
  if (userId.length > 0 && coachList.length > 0) {
    let coachName = '';
    const coachObject = coachList.find((item) => item.userId === userId);
    coachName = coachObject ? `${ coachObject.coachFirstName} ${coachObject.coachLastName }` : 'Unassigned';
    return coachName;
  }
  return '';
};

export const isNotEmpty = (value) => value !== null
  && value !== undefined
  && !(typeof value === 'string' && value.length === 0)
  && !(Array.isArray(value) && value.length === 0);

export const hexToRGBA = (hex, alpha = 1) => {
  const [r, g, b] = hex.match(/\w\w/g).map((x) => parseInt(x, 16));

  if ([r, g, b].includes(NaN)) {
    return `rgba(0,0,0,${alpha})`;
  }

  return `rgba(${r},${g},${b},${alpha})`;
};

export const filterAdaptor = ({ filtersObject, coachingTypeId, picklists }) => {
  let result = [];

  // Add defensive check for filtersObject
  if (!filtersObject || typeof filtersObject !== 'object') {
    return result;
  }

  const selected = filtersObject.selectedFilters || {};

  if (selected.status && Array.isArray(selected.status) && selected.status.length > 0) {
    let statusIds = selected.status
      .filter((filter) => filter && filter.id !== undefined && filter.id !== null)
      .map((filter) => filter.id);

    if (coachingTypeId && picklists?.status && Array.isArray(picklists.status)) {
      const validStatusIds = picklists.status
        .filter((status) => status && status.coachingTypeId === coachingTypeId)
        .map((status) => status.id);
      statusIds = statusIds.filter((id) => validStatusIds.includes(id));
    }

    if (statusIds.length > 0) {
      result = [...result, {
        fieldName: 'StatusId',
        values: statusIds,
      }];
    }
  }

  if (selected.coach && Array.isArray(selected.coach) && selected.coach.length > 0) {
    const coachUserIds = selected.coach
      .filter((filter) => filter && filter.value && filter.value.userId)
      .map((filter) => filter.value.userId);
    if (coachUserIds.length > 0) {
      result = [...result, {
        fieldName: 'CoachUserId',
        values: coachUserIds,
      }];
    }
  }

  if (selected.coachingSubType && Array.isArray(selected.coachingSubType) && selected.coachingSubType.length > 0) {
    const coachingTypeIds = selected.coachingSubType
      .filter((filter) => filter && filter.id !== undefined && filter.id !== null)
      .map((filter) => filter.id);
    if (coachingTypeIds.length > 0) {
      result = [...result, {
        fieldName: 'Products',
        values: coachingTypeIds,
      }];
    }
  }

  if (selected.assignedLead && Array.isArray(selected.assignedLead) && selected.assignedLead.length > 0) {
    const assignedLeadUserIds = selected.assignedLead
      .filter((filter) => filter && filter.value && filter.value.userId)
      .map((filter) => filter.value.userId);
    if (assignedLeadUserIds.length > 0) {
      result = [...result, {
        fieldName: 'CoachUserId',
        values: assignedLeadUserIds,
      }];
    }
  }

  // Customer account filter maps directly to the CustomerCompany field
  // in the /coachingrequest/list/v1 API. Each selected option's id is
  // the customer company name (for example, "TestCorp").
  if (selected.customerAccount && Array.isArray(selected.customerAccount) && selected.customerAccount.length > 0) {
    const customerCompanies = selected.customerAccount
      .filter((filter) => filter && filter.id !== undefined && filter.id !== null)
      .map((filter) => filter.id);
    if (customerCompanies.length > 0) {
      result = [...result, {
        fieldName: 'CustomerCompany',
        values: customerCompanies,
      }];
    }
  }
  return result;
};

export const getCoachingSubTypes = ({ data, id }) => {
  // Find the coaching type by id and return its coachingSubTypes array
  const coachingType = Object.values(data).find((item) => item.id === id);
  const rawSubTypes = coachingType && Array.isArray(coachingType.coachingSubTypes) ? coachingType.coachingSubTypes : [];
  // Use adaptPicklistToChip to ensure consistent format and deduplication
  return adaptPicklistToChip({ data: rawSubTypes });
};

export const areObjectsEqual = (obj1, obj2) => JSON.stringify(deepmerge(obj1, {})) === JSON.stringify(deepmerge(obj2, {}));

/**
 * Determines if a content item failed to lookup from Contentful
 * An item is considered to have failed lookup if it's missing the title field
 * @param {Object} item - The content item to check
 * @returns {boolean} - True if the item failed lookup, false otherwise
 */
export const isContentItemMissingData = (item) => {
  if (!item) return true;
  return !item.title || item.title === '';
};
